import { afterNextRender, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { BlogService } from './blog.service';
import { finalize, tap } from 'rxjs';
import { BlogCategory, BlogPost } from './blog.types';
import { MatPaginatorModule } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { PostComponent } from './post/post.component';
import { Router } from '@angular/router';
import { ResolutionService } from '../../shared/resolution.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { PagesService } from '../pages.service';

@Component({
  selector: 'app-blog',
  imports: [
    MatPaginatorModule,
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    PostComponent
  ],
  templateUrl: './blog.component.html',
  styleUrl: './blog.component.scss'
})
export class BlogComponent implements OnDestroy {

  //#region Services

  #blogService = inject(BlogService);

  #router = inject(Router);

  #resolutionService = inject(ResolutionService);

  #pagesService = inject(PagesService);

  //#endregion

  //#region Properties

  isLoading = signal(true);

  blogs = signal<BlogPost[]>([]);

  blog = signal<BlogPost | null>(null);

  categories = signal<BlogCategory[]>([{
    id: 0,
    name: 'All Post',
    isSelected: false,
    color: ''
  }]);

  total = signal(0);

  size = computed(() => this.#blogService.size());

  page = computed(() => this.#blogService.page());

  categoryId = computed(() => this.#blogService.categoryId());

  isMobile = computed(() => this.#resolutionService.isMobile());

  backgroundImage = computed(() => this.#getBgImage());

  pageSizeOptions = signal<number[]>([]);

  //#endregion

  //#region Constructors

  constructor() {
    this.#pagesService.restartSideMenu();
    this.#pagesService.showBreadcrumb.set(false);
    this.#getCategories();

    afterNextRender(() => {
      window.addEventListener('resize', () => {
        this.#setUpPageSize();
      });

      this.#setUpPageSize();
      this.#blogService.size.set(this.pageSizeOptions()[0]);
      this.#getAllPosts();
    });
  }

  ngOnDestroy(): void {
    this.#pagesService.showBreadcrumb.set(true);
  }

  //#endregion

  //#region Methods

  #getBgImage() {

    if (!this.blog())
      return '';

    const { desktop, mobile } = this.blog()?.banners ?? {};

    let img = this.blog()?.image || 'assets/images/product-placeholder.webp';

    if (this.isMobile() && mobile)
      img = mobile;
    else if (!this.isMobile() && desktop)
      img = desktop;

    return `url(${img})`;
  }

  #setPost(data: { blogs: BlogPost[], length: number }) {
    this.blogs.set(data?.blogs ?? []);
    this.total.set(data.length);
    this.blog.set(this.blogs().find(x => x.featured) ?? this.blogs()[0]);
  }

  #getAllPosts() {

    this.#blogService
      .getAll()
      .pipe(
        tap(response => {
          this.#setPost(response.data);
        })
      )
      .subscribe();
  }

  #getCategories() {

    this.isLoading.set(true);

    this.#blogService
      .getCategories()
      .pipe(
        tap(response => {

          this.categories.update(value => {
            value.push(...response.data);
            return value;
          });

          const index = this.categories().findIndex(x => x.id === this.#blogService.categoryId());
          this.categories()[index].isSelected = true;
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  #resetFilters(categoryId = 0) {
    this.#blogService.page.set(0);
    this.#blogService.categoryId.set(categoryId);
  }

  changeCategory(category: BlogCategory) {
    this.categories().forEach(x => x.isSelected = false);
    category.isSelected = true;
    this.#resetFilters(category.id);
    this.#getAllPosts();
  }

  handlePageEvent(event: any) {

    if (this.#blogService.size() !== event.pageSize) {
      this.#blogService.size.set(event.pageSize);
      this.#blogService.page.set(0);
    } else {
      this.#blogService.page.set(event.pageIndex);
    }

    this.#getAllPosts();
  }

  goToDetail(blog: BlogPost | null) {
    this.#router.navigate([`/pages/blog-post/${blog?.url}`]);
  }

  #setUpPageSize() {

    const oldIndex = this.pageSizeOptions()
      .findIndex(x => x === this.#blogService.size());

    const width = Math.floor(window.visualViewport?.width ?? 0);

    if (width > 603 && width < 908)
      this.pageSizeOptions.set([4, 10, 50, 100]);
    else
      this.pageSizeOptions.set([6, 12, 30, 90]);

    if (!this.#blogService.size()) return;

    const newValue = this.pageSizeOptions()[oldIndex];

    if (newValue !== this.#blogService.size()) {
      this.#blogService.size.set(newValue);
      this.#getAllPosts();
    }
  }

  //#endregion
}
